import { errorlog } from '@/libs/error-logs';
import { toast_error, toast_success } from '@/libs/toast-wrappers';
import { toError } from '@/utils/errors';

import { GET, POST, DELETE } from './base';
import { ENDPOINTS } from './endpoints';

export type AISearchDoc = {
  openai_file_id: string;
  file_name: string;
  file_size?: number;
  upload_date?: string;
  s3_key?: string;
};

export type AISearchDocsResponse = {
  data: AISearchDoc[];
};

export async function getAISearchDocs(): Promise<AISearchDoc[]> {
  try {
    const res = await GET<AISearchDocsResponse>(ENDPOINTS.AI_SEARCH_DOCS);
    return res.data;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/ai-search-docs/getAISearchDocs',
      'Error fetching AI search docs'
    );
    toast_error('Error fetching AI search documents');
    return Promise.reject(toError(error));
  }
}

export async function uploadAISearchDoc(file: File) {
  try {
    const form = new FormData();
    form.append('file', file);
    const res = await POST<{ id: string }>(ENDPOINTS.AI_SEARCH_DOCS, form);
    toast_success('File uploaded');
    return res;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/ai-search-docs/uploadAISearchDoc',
      'Error uploading AI search doc'
    );
    toast_error('Error uploading file');
    return Promise.reject(toError(error));
  }
}

export async function deleteAISearchDoc(fileId: string) {
  try {
    await DELETE(ENDPOINTS.AI_SEARCH_DOC_FILE, { parameters: { fileId } });
    toast_success('File deleted');
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/ai-search-docs/deleteAISearchDoc',
      'Error deleting AI search doc'
    );
    toast_error('Error deleting file');
    return Promise.reject(toError(error));
  }
}

/**
 * Obtain a presigned preview URL using the file name.
 * GET /ai-search/files/preview-url?file_name=<name>&expires_in=<seconds>
 */
export async function getAISearchDocPreviewUrl(
  fileName: string,
  expiresIn = 3600
): Promise<string> {
  try {
    const res = await GET<{
      data?: { file_name: string; s3_key?: string; url?: string; expires_in?: number };
      message?: string;
      success?: boolean;
    }>(ENDPOINTS.AI_SEARCH_DOC_PREVIEW_URL, {
      query: { file_name: fileName, expires_in: String(expiresIn) },
    });

    const url = res?.data?.url;
    if (typeof url === 'string' && url.length > 0) return url;

    toast_error('Preview URL not available for this file');
    throw new Error('Preview URL not available');
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/ai-search-docs/getAISearchDocPreviewUrl',
      'Error retrieving AI search preview URL'
    );
    toast_error('Error retrieving document link');
    return Promise.reject(toError(error));
  }
}
